import { motion } from 'motion/react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { transitions } from '../lib/motion';
import { AreaChart, DonutChart, HeroFigure, RankedBars } from '../components/charts';
import {
  CategoryIcon,
  IconChevronRight,
  IconLink,
  IconPlus,
  IconReceipt,
  IconWarning,
} from '../components/icons';
import { LargeTitle } from '../components/layout';
import { ReceiptRow, categoryName } from '../components/receipts';
import { Button, Card, EmptyState, SectionHeader, Skeleton } from '../components/ui';
import { useI18n } from '../i18n';
import type { Summary, User } from '../lib/api';
import { useLedgerContext } from '../lib/ledger-context';
import { useExpenses, useSummary } from '../lib/queries';
import { accentVar } from '../lib/ui';

function greetingKey(now = new Date()) {
  const hour = now.getHours();
  if (hour < 12) return 'dashboard.greetingMorning' as const;
  if (hour < 18) return 'dashboard.greetingAfternoon' as const;
  return 'dashboard.greetingEvening' as const;
}

/** Running total of what is claimable, so the line only ever climbs or flattens. */
function cumulativePoints(byMonth: Summary['byMonth'], formatMonth: (month: string) => string) {
  let running = 0;
  return byMonth.map((month) => {
    running += month.availableCents;
    // Axis labels are formatted per locale here rather than in the chart, so
    // `2026-08` never reaches a reader as `2026-08`.
    return { key: month.month, label: formatMonth(month.month), value: running };
  });
}

function AttentionCard({
  tone,
  title,
  body,
  to,
}: {
  tone: 'caution' | 'accent';
  title: string;
  body: string;
  to: string;
}) {
  return (
    <Link to={to} className="press block">
      <Card className="flex items-start gap-3">
        {/* A warning triangle on a neutral "set this up" prompt reads as an
            error. Caution warns; accent invites. */}
        <span
          className="mt-0.5 shrink-0"
          style={{ color: tone === 'caution' ? 'var(--caution)' : 'var(--accent)' }}
        >
          {tone === 'caution' ? <IconWarning size={20} /> : <IconLink size={20} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-medium text-[var(--ink)]">{title}</div>
          <p className="mt-0.5 text-[13px] leading-snug text-[var(--ink-secondary)]">{body}</p>
        </div>
        <IconChevronRight size={18} className="mt-0.5 shrink-0 text-[var(--ink-muted)]" />
      </Card>
    </Link>
  );
}

export function DashboardScreen({ user, paperlessReady }: { user: User; paperlessReady: boolean }) {
  const { t, money, date, locale } = useI18n();
  const { current, filterIds, ledgers, isLoading: ledgersLoading } = useLedgerContext();

  const summaryQuery = useSummary({ ledgerId: filterIds });
  const recentQuery = useExpenses({ ledgerId: filterIds, pageSize: 5, sort: 'added_desc' });

  const summary = summaryQuery.data;
  const points = useMemo(
    () =>
      summary ? cumulativePoints(summary.byMonth, (month) => date(`${month}-01`, 'monthYear')) : [],
    [summary, date],
  );

  const categoryItems = useMemo(
    () =>
      (summary?.byCategory ?? [])
        .filter((row) => row.spentCents > 0)
        .map((row) => ({
          id: row.categoryId ?? 'none',
          label: row.nameEn
            ? categoryName({ nameEn: row.nameEn, nameZh: row.nameZh ?? row.nameEn }, locale)
            : t('common.uncategorized'),
          color: accentVar(row.color),
          value: row.spentCents,
          secondary: row.availableCents,
          icon: <CategoryIcon glyph={row.icon} size={16} />,
        })),
    [summary, locale, t],
  );

  const personItems = useMemo(
    () =>
      (summary?.byPerson ?? [])
        .filter((row) => row.spentCents > 0)
        .map((row) => ({
          id: row.personId ?? 'none',
          label: row.name ?? t('common.unknown'),
          color: accentVar(row.color),
          value: row.spentCents,
        })),
    [summary, t],
  );

  // A brand-new account, or one whose access was revoked, genuinely has
  // nothing — and should be told so plainly rather than shown a dashboard of
  // zeroes that looks like a bug.
  if (!ledgersLoading && ledgers.length === 0) {
    return (
      <>
        <LargeTitle title={t(greetingKey(), { name: user.name.split(' ')[0] ?? user.name })} />
        <div className="px-4 lg:px-8">
          <Card>
            <EmptyState
              icon={<IconReceipt size={38} />}
              title={t('ledger.noAccessTitle')}
              body={t('ledger.noAccessBody')}
              action={
                <Link to="/settings">
                  <Button variant="secondary">{t('ledger.create')}</Button>
                </Link>
              }
            />
          </Card>
        </div>
      </>
    );
  }

  if (summaryQuery.isLoading || ledgersLoading) {
    return (
      <>
        <LargeTitle title={t('nav.overview')} />
        <div className="space-y-4 px-4 lg:px-8">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-56 w-full" />
        </div>
      </>
    );
  }

  const headline = summary?.headline;
  const isEmpty = !headline || headline.count === 0;

  return (
    <>
      <LargeTitle
        title={t(greetingKey(), { name: user.name.split(' ')[0] ?? user.name })}
        subtitle={current?.name}
      />

      <div className="space-y-6 px-4 pt-3 lg:px-8">
        {/*
          Above everything, including the empty state: without Paperless, Cairn
          cannot hold a single document, and a brand-new instance has no
          receipts *and* no connection. Burying this under "no receipts yet"
          would hide the one thing standing between the two.
        */}
        {!paperlessReady && user.role === 'admin' ? (
          <AttentionCard
            tone="accent"
            title={t('dashboard.connectPaperless')}
            body={t('dashboard.connectPaperlessBody')}
            to="/settings"
          />
        ) : null}

        {isEmpty ? (
          <Card>
            <EmptyState
              title={t('dashboard.emptyTitle')}
              body={t('dashboard.emptyBody')}
              action={
                <Link to="/add">
                  <Button icon={<IconPlus size={18} />}>{t('dashboard.emptyAction')}</Button>
                </Link>
              }
            />
          </Card>
        ) : (
          <>
            {/* The headline. Everything else on this page is context for it. */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={transitions.enter}
            >
              <Card className="overflow-hidden">
                <HeroFigure
                  label={t('dashboard.availableToReimburse')}
                  value={money(headline.availableCents, { round: true })}
                  caption={t('dashboard.availableExplainer')}
                  footnote={
                    <div className="grid grid-cols-2 gap-3 border-t border-[var(--line)] pt-4">
                      <div>
                        <div className="text-[12px] text-[var(--ink-secondary)]">
                          {t('dashboard.totalSpent')}
                        </div>
                        <div className="numeric mt-0.5 text-[17px] font-semibold text-[var(--ink)]">
                          {money(headline.spentCents, { round: true })}
                        </div>
                      </div>
                      <div>
                        <div className="text-[12px] text-[var(--ink-secondary)]">
                          {t('dashboard.alreadyClaimed')}
                        </div>
                        <div className="numeric mt-0.5 text-[17px] font-semibold text-[var(--ink)]">
                          {money(headline.reimbursedCents, { round: true })}
                        </div>
                      </div>
                    </div>
                  }
                />

                {headline.oldestClaimableDate ? (
                  <p className="mt-4 text-[12px] leading-snug text-[var(--ink-muted)]">
                    {t('dashboard.oldestClaimable')}: {date(headline.oldestClaimableDate)} ·{' '}
                    {t('dashboard.oldestClaimableHint')}
                  </p>
                ) : null}
              </Card>
            </motion.div>

            {headline.missingDocuments > 0 || headline.strandedCents > 0 ? (
              <section>
                <SectionHeader title={t('dashboard.needsAttention')} />
                <div className="space-y-2.5">
                  {headline.missingDocuments > 0 ? (
                    <AttentionCard
                      tone="caution"
                      title={t('dashboard.missingDocuments', {
                        count: headline.missingDocuments,
                      })}
                      body={t('dashboard.missingDocumentsHelp')}
                      to="/receipts?hasDocument=false"
                    />
                  ) : null}
                  {headline.strandedCents > 0 && summary?.hsaEstablishedDate ? (
                    <AttentionCard
                      tone="caution"
                      title={t('dashboard.strandedWarning')}
                      body={t('dashboard.strandedHelp', {
                        amount: money(headline.strandedCents),
                        date: date(summary.hsaEstablishedDate),
                      })}
                      to={`/receipts?to=${summary.hsaEstablishedDate}`}
                    />
                  ) : null}
                </div>
              </section>
            ) : null}

            {points.length >= 2 ? (
              <Card>
                <AreaChart
                  title={t('dashboard.spendingOverTime')}
                  description={t('dashboard.cumulativeClaimable')}
                  points={points}
                  formatValue={(value) => money(value, { round: true })}
                />
              </Card>
            ) : null}

            {categoryItems.length > 0 ? (
              <Card>
                <RankedBars
                  title={t('dashboard.byCategory')}
                  items={categoryItems}
                  formatValue={(value) => money(value, { round: true })}
                />
              </Card>
            ) : null}

            {personItems.length > 1 ? (
              <Card>
                <DonutChart
                  title={t('dashboard.byPerson')}
                  items={personItems}
                  formatValue={(value) => money(value, { round: true })}
                  centerLabel={t('dashboard.totalSpent')}
                  centerValue={money(headline.spentCents, { round: true })}
                />
              </Card>
            ) : null}

            <section>
              <SectionHeader
                title={t('dashboard.recentReceipts')}
                action={
                  <Link
                    to="/receipts"
                    className="text-[13px] font-medium text-[var(--accent)] hover:underline"
                  >
                    {t('dashboard.viewAll')}
                  </Link>
                }
              />
              <div className="card divide-y divide-[var(--line)] overflow-hidden">
                {recentQuery.data?.expenses.map((expense) => (
                  <ReceiptRow key={expense.id} expense={expense} />
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </>
  );
}
